import { useMemo } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { USD, compact, magnitude } from '../../lib/format';
import { C, ink } from '../../lib/theme';
import type { ContractionRow } from '../../lib/types';
import { ChartEmpty, ChartTooltip, Legend, axisStyle, bucketLabel, gridStroke, useCrosshair } from './shared';

/**
 * Gráfico 5 — Contraction vs MRR Pendente (PRD §9.5, atende ao Cenário 1).
 * Barra = contraction_mrr perdido no período; linha = mrr_ativo_dia agregado,
 * ou seja, quanto ainda resta ativo. Responde "a contração está comendo que
 * fatia do que ainda temos". Compartilha o crosshair com a Tendência (§9-D).
 */
export function ContractionChart({ rows, granularity }: { rows: ContractionRow[]; granularity: string }) {
  const crosshair = useCrosshair();

  const data = useMemo(() => {
    const map = new Map<string, { bucket: string; label: string; contraction: number; ativo: number }>();
    for (const r of rows) {
      const key = String(r.bucket);
      if (!map.has(key)) map.set(key, { bucket: key, label: bucketLabel(key, granularity), contraction: 0, ativo: 0 });
      const slot = map.get(key)!;
      slot.contraction += magnitude(r.contraction_mrr);
      slot.ativo += magnitude(r.mrr_pendente);
    }
    return [...map.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
  }, [rows, granularity]);

  if (data.length === 0) return <ChartEmpty />;

  const activeLabel = data.find((d) => d.bucket === crosshair.bucket)?.label;

  return (
    <>
      <ResponsiveContainer width="100%" height="88%">
        <ComposedChart
          data={data} margin={{ top: 6, right: 4, left: -14, bottom: 0 }}
          onMouseMove={(s) => {
            const label = (s as { activeLabel?: string }).activeLabel;
            crosshair.set(data.find((d) => d.label === label)?.bucket ?? null);
          }}
          onMouseLeave={() => crosshair.set(null)}
        >
          <CartesianGrid stroke={gridStroke} vertical={false} />
          <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis yAxisId="left" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => compact(v)} width={52} />
          <YAxis yAxisId="right" orientation="right" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={(v) => compact(v)} width={52} />
          <Tooltip
            cursor={{ fill: ink(0.05) }}
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <ChartTooltip
                  label={String(label)}
                  rows={payload.map((p) => ({ name: String(p.name), value: USD(p.value as number), color: String(p.color) }))}
                />
              ) : null
            }
          />
          {/* Crosshair vindo do OUTRO gráfico — é o que sincroniza os dois. */}
          {activeLabel && (
            <ReferenceLine yAxisId="left" x={activeLabel} stroke={C.accent} strokeOpacity={0.5} strokeDasharray="3 3" />
          )}
          <Bar yAxisId="left" dataKey="contraction" name="contraction perdido (esq.)" fill={C.amber} radius={[4, 4, 0, 0]} barSize={26} />
          <Line yAxisId="right" type="monotone" dataKey="ativo" name="MRR ainda ativo (dir.)" stroke={C.green} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C.green }} />
        </ComposedChart>
      </ResponsiveContainer>
      <Legend items={[
        { label: 'contraction perdido (esq.)', color: C.amber },
        { label: 'MRR ainda ativo (dir.)', color: C.green },
      ]} />
    </>
  );
}
